import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './AdminDashboard.css';

function AdminDashboard() {
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuthAndLoadData();
  }, []);

  // Suchfilter anwenden
  useEffect(() => {
    if (searchTerm) {
      const filtered = submissions.filter(sub =>
        sub.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.kostenstelle.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.serviceName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredSubmissions(filtered);
    } else {
      setFilteredSubmissions(submissions);
    }
  }, [searchTerm, submissions]);

  const checkAuthAndLoadData = async () => {
    try {
      // Auth prüfen
      const authResponse = await axios.get('/api/admin/check', {
        withCredentials: true
      });

      if (!authResponse.data.authenticated) {
        navigate('/admin');
        return;
      }

      // Daten laden
      await loadSubmissions();
    } catch (error) {
      console.error('Fehler beim Laden:', error);
      navigate('/admin');
    }
  };

  const loadSubmissions = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get('/api/admin/submissions', {
        withCredentials: true
      });

      if (response.data.success) {
        setSubmissions(response.data.submissions);
        setFilteredSubmissions(response.data.submissions);
      }
    } catch (error) {
      setError('Fehler beim Laden der Anträge.');
      console.error('Fehler:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post('/api/admin/logout', {}, {
        withCredentials: true
      });
      navigate('/admin');
    } catch (error) {
      console.error('Logout-Fehler:', error);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sorted = [...filteredSubmissions].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredSubmissions(sorted);
  };

  const exportToCSV = () => {
    const headers = ['ID', 'Datum', 'Name', 'Kostenstelle', 'Service', 'Checkbox 1', 'Checkbox 2', 'PDF'];
    const rows = filteredSubmissions.map(sub => [
      sub.id,
      new Date(sub.createdAt).toLocaleString('de-DE'),
      sub.name,
      sub.kostenstelle,
      sub.serviceName,
      sub.checkbox1Confirmed ? 'Ja' : 'Nein',
      sub.checkbox2Confirmed ? 'Ja' : 'Nein',
      sub.pdfFilename
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `erstattungsantraege_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="admin-dashboard-container">
        <div className="loading">Lädt Daten...</div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-container">
      <div className="dashboard-header">
        <h2>Admin-Dashboard</h2>
        <button onClick={handleLogout} className="logout-button">
          Abmelden
        </button>
      </div>

      {error && (
        <div className="error-message">{error}</div>
      )}

      <div className="dashboard-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Suche nach Name, Kostenstelle oder Service..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <button onClick={exportToCSV} className="export-button">
          Als CSV exportieren
        </button>
        <button onClick={loadSubmissions} className="refresh-button">
          Aktualisieren
        </button>
      </div>

      <div className="submissions-stats">
        <p>Gesamt: <strong>{submissions.length}</strong> Anträge</p>
        {searchTerm && (
          <p>Gefiltert: <strong>{filteredSubmissions.length}</strong> Anträge</p>
        )}
      </div>

      <div className="table-container">
        <table className="submissions-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('id')}>
                ID {sortConfig.key === 'id' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('createdAt')}>
                Datum {sortConfig.key === 'createdAt' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('name')}>
                Name {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('kostenstelle')}>
                Kostenstelle {sortConfig.key === 'kostenstelle' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('serviceName')}>
                Service {sortConfig.key === 'serviceName' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th>PDF</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {filteredSubmissions.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">
                  {searchTerm ? 'Keine Anträge gefunden.' : 'Noch keine Anträge eingereicht.'}
                </td>
              </tr>
            ) : (
              filteredSubmissions.map((submission) => (
                <tr key={submission.id}>
                  <td>{submission.id}</td>
                  <td>{formatDate(submission.createdAt)}</td>
                  <td>{submission.name}</td>
                  <td>{submission.kostenstelle}</td>
                  <td>{submission.serviceName}</td>
                  <td>
                    {submission.pdfPath.split(', ').map((pdfPath, idx) => {
                      const pdfName = submission.pdfFilename.split(', ')[idx] || `Datei ${idx + 1}`;
                      return (
                        <button
                          key={idx}
                          onClick={() => window.open(`/api/admin/download/${pdfPath.trim()}`, '_blank')}
                          className="download-button-small"
                          title={`${pdfName} herunterladen`}
                          style={{ marginRight: '5px', marginBottom: '5px' }}
                        >
                          📄 {submission.pdfPath.split(', ').length > 1 ? `PDF ${idx + 1}` : 'Download'}
                        </button>
                      );
                    })}
                  </td>
                  <td>
                    <button
                      onClick={() => setSelectedSubmission(submission)}
                      className="view-button"
                    >
                      Details
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Detail-Modal */}
      {selectedSubmission && (
        <div className="modal-overlay" onClick={() => setSelectedSubmission(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Antragsdetails - ID {selectedSubmission.id}</h3>
              <button
                onClick={() => setSelectedSubmission(null)}
                className="modal-close"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <strong>Eingereicht am:</strong>
                <span>{formatDate(selectedSubmission.createdAt)}</span>
              </div>
              <div className="detail-row">
                <strong>Name:</strong>
                <span>{selectedSubmission.name}</span>
              </div>
              <div className="detail-row">
                <strong>Kostenstelle:</strong>
                <span>{selectedSubmission.kostenstelle}</span>
              </div>
              <div className="detail-row">
                <strong>Service:</strong>
                <span>{selectedSubmission.serviceName}</span>
              </div>
              <div className="detail-row">
                <strong>Einkauf mit Verwaltung abgesprochen:</strong>
                <span className={selectedSubmission.checkbox1Confirmed ? 'status-yes' : 'status-no'}>
                  {selectedSubmission.checkbox1Confirmed ? '✓ Ja' : '✗ Nein'}
                </span>
              </div>
              <div className="detail-row">
                <strong>Bankdaten hinterlegt:</strong>
                <span className={selectedSubmission.checkbox2Confirmed ? 'status-yes' : 'status-no'}>
                  {selectedSubmission.checkbox2Confirmed ? '✓ Ja' : '✗ Nein'}
                </span>
              </div>
              <div className="detail-row">
                <strong>PDF-Datei{selectedSubmission.pdfFilename.split(', ').length > 1 ? 'en' : ''}:</strong>
                <span>{selectedSubmission.pdfFilename}</span>
              </div>
              <div className="modal-actions">
                {selectedSubmission.pdfPath.split(', ').map((pdfPath, idx) => {
                  const pdfName = selectedSubmission.pdfFilename.split(', ')[idx] || `Datei ${idx + 1}`;
                  return (
                    <button
                      key={idx}
                      onClick={() => window.open(`/api/admin/download/${pdfPath.trim()}`, '_blank')}
                      className="download-button"
                      style={{ marginRight: '10px', marginBottom: '10px' }}
                    >
                      {selectedSubmission.pdfPath.split(', ').length > 1 ? `PDF ${idx + 1} (${pdfName})` : 'PDF herunterladen'}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
